// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A continuous voice: one looping buffer, aimed once a frame.
//
// Three sounds in this product are states rather than events — the ball rolling (M3-24), the
// plunger being drawn and the tilt bob swinging (M3-26) — and all three are the same mechanism:
// a buffer playing on a loop, with its level, its pan, its playback rate and optionally a
// bandpass moved every frame by whatever the simulation says is happening. What differs between
// them is only the arithmetic that decides those numbers, which is each caller's business.
//
// Extracted when the second and third arrived. The node plumbing is not interesting and it is
// not short: build four nodes, wire them, start, glide five parameters, tear all of it down
// without leaking a source. Three copies of that is three chances to forget the teardown, and a
// leaked looping source is silent in a test and permanent in a game.
//
// **Built lazily and torn down completely.** A machine with six ball slots and one ball in play
// should hold one source, not six silent ones; a plunger nobody is touching should hold none.

import type {
  AudioBufferLike,
  AudioNodeLike,
  AudioParamLike,
  AudioPlaybackContext,
  BiquadFilterNodeLike,
  BufferSourceLike,
  GainNodeLike,
  PannerNodeLike,
  StereoPannerLike,
} from './webAudio'
import { asHrtf } from './webAudio'
import type { Spot } from './space'

/** How quickly a parameter chases its target. One frame at 60 fps, so it glides rather than steps. */
export const LOOP_GLIDE = 0.016

/** What a caller wants the voice to be doing this frame. */
export interface LoopAim {
  /** Linear gain. Zero or less stops the voice. */
  readonly level: number
  readonly pan: number
  /** Playback rate. 1 leaves the buffer at its own pitch. */
  readonly rate?: number
  /** Bandpass centre, for a voice built with a filter. Ignored otherwise. */
  readonly centreHz?: number
  readonly q?: number
  /**
   * Where the sound is, in the machine's own frame (M9-48).
   *
   * A bed is the one voice in this product that genuinely *moves*: a rolling ball crosses the
   * whole playfield while one source plays. So this is aimed every frame like the level and the
   * rate, rather than fixed at the node the way a one-shot's is.
   *
   * Absent means not placed, and so does a context with no listener — see `spatialise`.
   */
  readonly at?: Spot
}

/**
 * Where the player's ears are, in the machine's own frame.
 *
 * A getter rather than a value, and deliberately: a bed outlives the moment it was built, so a
 * voice handed a head would go on believing in it after the player left the headset. The browser
 * has this mistake recorded once already — the Quest's `MachineView` captured a machine object
 * where the browser's took `() => this.session.loaded` and could not.
 */
export type HeadAt = () => { x: number; y: number; z: number } | undefined

export class LoopVoice {
  private source: BufferSourceLike | undefined
  private filter: BiquadFilterNodeLike | undefined
  private gain: GainNodeLike | undefined
  private panner: StereoPannerLike | PannerNodeLike | undefined
  /** Whether `panner` is an HRTF `PannerNode` rather than the stereo collapse. */
  private spatial = false

  /**
   * @param filtered Insert a bandpass between the source and the gain. The rolling bed needs
   *   one because its centre frequency *is* the sound; a plunger scrape and a bob's ring are
   *   already the right shape in the buffer and a filter would be a node doing nothing.
   * @param head Where the listener is, asked once a frame. The default is "there is no head",
   *   which is a flat screen and is what the arcade's own room bed stays on for ever — 07 §8
   *   makes ambience non-positional in as many words.
   */
  constructor(
    private readonly context: AudioPlaybackContext,
    private readonly buffer: AudioBufferLike,
    private readonly destination: AudioNodeLike,
    private readonly filtered = false,
    private readonly head: HeadAt = () => undefined,
  ) {}

  /**
   * Whether this frame's aim wants an HRTF panner.
   *
   * Three conditions and all of them necessary: a head, a context that can build a panner, and a
   * position for this voice. A stub has no `createPanner`, a flat screen has no head, and a bed
   * aimed with no `at` has no point — any one of them missing is the stereo collapse, which is
   * what every bed in this product did before M9-48.
   */
  private spatialise(at: Spot | undefined): boolean {
    return at !== undefined && this.context.createPanner !== undefined && this.head() !== undefined
  }

  get playing(): boolean {
    return this.source !== undefined
  }

  /**
   * Aim the voice, starting or stopping it as the level requires.
   *
   * Safe to call every frame with a zero level: a voice that is not playing and is not wanted
   * costs one comparison.
   */
  aim(target: LoopAim): void {
    if (target.level <= 0) {
      this.stop()
      return
    }
    // **A head can arrive and leave in the middle of a bed's life** — a player enters the headset
    // while a ball is rolling, or takes it off — and the two panners are different nodes, so the
    // voice is rebuilt rather than re-wired. Once, at the transition, which is the one instant a
    // discontinuity in a quiet bed is not the most noticeable thing happening.
    const spatial = this.spatialise(target.at)
    if (this.source !== undefined && spatial !== this.spatial) this.stop()
    if (this.source === undefined) this.start(spatial)

    const now = this.context.currentTime
    approach(this.gain?.gain, target.level, now)
    if (this.spatial) {
      const at = target.at
      const panner = this.panner as PannerNodeLike | undefined
      if (at !== undefined && panner !== undefined) {
        // Web Audio is x right, y up, −z forward; the machine's frame is x right, y up the
        // playfield, z above it. The same conversion `MachineAudio.placeVoice` does, and the
        // ramp is `LOOP_GLIDE` for the same reason the level is: a ball crossing the playfield
        // that jumps its position once a frame reads as a stutter rather than as movement.
        approach(panner.positionX, at.x, now)
        approach(panner.positionY, at.z, now)
        approach(panner.positionZ, -at.y, now)
      }
    } else {
      approach((this.panner as StereoPannerLike | undefined)?.pan, clamp(target.pan, -1, 1), now)
    }
    if (target.rate !== undefined) approach(this.source?.playbackRate, target.rate, now)
    if (this.filtered) {
      if (target.centreHz !== undefined) approach(this.filter?.frequency, target.centreHz, now)
      if (target.q !== undefined) approach(this.filter?.Q, target.q, now)
    }
  }

  /**
   * Silence and tear down.
   *
   * The nodes go immediately rather than after a release ramp. Every voice built on this is a
   * quiet bed whose stop is inaudible, and keeping four nodes alive per voice waiting for a ramp
   * that may be interrupted by the machine being reset is the kind of bookkeeping that leaks a
   * source per ball per game.
   */
  stop(): void {
    const source = this.source
    if (source !== undefined) {
      source.onended = null
      source.stop()
      source.disconnect()
    }
    this.filter?.disconnect()
    this.gain?.disconnect()
    this.panner?.disconnect()
    this.source = undefined
    this.filter = undefined
    this.gain = undefined
    this.panner = undefined
  }

  private start(spatial: boolean): void {
    const source = this.context.createBufferSource()
    source.buffer = this.buffer
    source.loop = true
    const gain = this.context.createGain()
    // From silence, so the voice fades in over one glide rather than arriving at full level.
    gain.gain.value = 0
    const create = spatial ? this.context.createPanner : undefined
    const panner = create === undefined ? this.context.createStereoPanner() : asHrtf(create.call(this.context))
    this.spatial = create !== undefined

    let head: AudioNodeLike = source
    if (this.filtered) {
      const filter = this.context.createBiquadFilter()
      filter.type = 'bandpass'
      source.connect(filter)
      head = filter
      this.filter = filter
    }
    head.connect(gain)
    gain.connect(panner)
    panner.connect(this.destination)
    source.start()

    this.source = source
    this.gain = gain
    this.panner = panner
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

/** `setTargetAtTime` on a parameter that may not exist yet. */
function approach(param: AudioParamLike | undefined, value: number, now: number): void {
  param?.setTargetAtTime(value, now, LOOP_GLIDE)
}
